import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  closeModal,
  isBlank,
  submitAnnualPlannerAction,
} from "../../../utils/annual-planner-helpers";
import InputColor from "../../../views/dashboard/settings/branding/InputColor";
import Modal from "../../modal-new";
import ConfirmationModal from "../ConfirmationModal";

// Opens straight into the editable form - there is no separate read-only
// preview step any more (per design: clicking a phase name should let you
// edit or delete it immediately, not require a second "Edit" click first).
// Delete has its own confirm step too, but as a *different* modal
// (ConfirmationModal, same component every other "delete X" flow in the app
// uses) rather than a warning banner squeezed into this same form - deleting
// a phase needs no more context than its name, already implied by which chip
// you clicked.
export const EditIndividualPhaseModal = ({
  athleteId,
  phase,
  organizationCoachId,
  track = "training",
}) => {
  const { t } = useTranslation(["coaching-portal", "common"]);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);

  const [phaseState, setPhaseState] = useState({
    phaseName: phase.phaseName,
    phaseColor: phase.phaseColor,
  });

  const { phaseName: statePhaseName, phaseColor: statePhaseColor } = phaseState;
  const isUnchanged =
    statePhaseName === phase.phaseName && statePhaseColor === phase.phaseColor;

  const handleChange = (input, payload) => {
    setPhaseState({ ...phaseState, [input]: payload });
  };

  const handleRemove = (e) => {
    e.preventDefault();
    submitAnnualPlannerAction(
      "/actions/annual-planner/remove-athlete-phase",
      { athleteId, phaseName: phase.phaseName, track },
      organizationCoachId
    );
  };

  const handleUpdate = (e) => {
    e.preventDefault();
    if (isBlank(statePhaseName) || !statePhaseColor || isUnchanged) return;

    submitAnnualPlannerAction(
      "/actions/annual-planner/update-athlete-phase",
      {
        athleteId,
        phaseName: statePhaseName,
        phaseColor: statePhaseColor,
        prevName: phase.phaseName,
        track,
      },
      organizationCoachId
    );
  };

  if (isConfirmingDelete) {
    return (
      <ConfirmationModal
        isOpen
        className="delete-phase-modal"
        icon="delete"
        title={t("editIndividualPhaseModal.deleteConfirmTitle")}
        contentText={t("editIndividualPhaseModal.deleteConfirmMessage")}
        cancelButtonText={t("common:cancel")}
        cancelButtonAction={() => setIsConfirmingDelete(false)}
        confirmButtonText={t("common:delete")}
        confirmButtonAction={handleRemove}
      />
    );
  }

  return (
    <Modal
      className="annual-planner-modal individual-phase-modal"
      title={t("editIndividualPhaseModal.titleEditPhase")}
    >
      <div className="body">
        <form
          className="annual-planner-modal-form individual-phase-modal-form"
          encType="multipart/form-data"
        >
          <div role="wrapper" className="phases-labels">
            <label>{t("editIndividualPhaseModal.phaseNameLabel")}</label>
            <label>{t("editIndividualPhaseModal.colorLabel")}</label>
          </div>
          <div role="wrapper" className="phase">
            <input
              autoFocus
              type="text"
              name="phaseName"
              className="phase-name text-input"
              placeholder={t("editIndividualPhaseModal.placeholderPhaseName")}
              defaultValue={phaseState.phaseName}
              onChange={({ target: { value } }) =>
                handleChange("phaseName", value)
              }
            />
            <InputColor
              onChange={({ target: { value } }) =>
                handleChange("phaseColor", value)
              }
              value={phaseState.phaseColor}
              customClassNames={"phase-color"}
            />
          </div>
        </form>
      </div>
      <footer className="individual-phase-footer">
        <button
          type="button"
          data-action="delete"
          onClick={() => setIsConfirmingDelete(true)}
        >
          <img src="/img/new-icons/red-trash.svg" />
          {t("common:delete")}
        </button>
        <button type="button" data-button-type="secondary" onClick={closeModal}>
          {t("common:cancel")}
        </button>
        <button
          type="button"
          data-button-type="primary"
          onClick={handleUpdate}
          disabled={isBlank(statePhaseName) || !statePhaseColor || isUnchanged}
        >
          {t("common:save")}
        </button>
      </footer>
    </Modal>
  );
};
